import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  GiftIcon,
  XMarkIcon,
  QrCodeIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline'
import { bundleService } from '../../services/bundleService'
import { productService } from '../../services/productService'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'

interface Bundle {
  bundle_id: number
  bundle_name: string
  bundle_sku: string
  description: string
  is_active: boolean
  bundle_components?: BundleItem[]
  calculations?: {
    bundle_price: number
  }
}

interface BundleItem {
  bundle_item_id?: number
  product_id: number
  quantity: number
  product?: {
    product_name: string
    sku: string
    sale_price: number
  }
}

export const BundlesPage = () => {
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingBundle, setEditingBundle] = useState<Bundle | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingBundle, setDeletingBundle] = useState<Bundle | null>(null)
  const [productSearch, setProductSearch] = useState('')
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    bundle_name: '',
    bundle_sku: '',
    description: '',
    is_active: true,
    components: [] as BundleItem[],
  })

  const [barcodeInput, setBarcodeInput] = useState('')
  const barcodeInputRef = useRef<HTMLInputElement>(null)

  const { data: bundlesData, isLoading } = useQuery({
    queryKey: ['bundles', search],
    queryFn: () => bundleService.getAll({ search }),
  })

  const { data: productsData } = useQuery({
    queryKey: ['products', 'bundle-search', productSearch],
    queryFn: () => productService.getAll({ search: productSearch, limit: 20 }),
    enabled: productSearch.length > 0,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => bundleService.create(data),
    onSuccess: () => {
      toast.success('Bundle created successfully')
      queryClient.invalidateQueries({ queryKey: ['bundles'] })
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create bundle')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => bundleService.update(id, data),
    onSuccess: () => {
      toast.success('Bundle updated successfully')
      queryClient.invalidateQueries({ queryKey: ['bundles'] })
      closeModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update bundle')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => bundleService.delete(id),
    onSuccess: () => {
      toast.success('Bundle deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['bundles'] })
      setShowDeleteModal(false)
      setDeletingBundle(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete bundle')
    },
  })

  const bundles = bundlesData?.data?.data?.items || []
  const availableProducts = productsData?.data?.data?.items || []

  const openModal = (bundle?: any) => {
    if (bundle) {
      setEditingBundle(bundle)
      // Map backend bundle_components to frontend components shape
      const components = bundle.bundle_components?.map((comp: any) => ({
        product_id: comp.product_id,
        quantity: comp.quantity,
        product: {
          product_name: comp.products?.product_name,
          sku: comp.products?.sku,
          sale_price: Number(comp.products?.retail_price || comp.products?.sale_price || 0),
        },
      })) || []
      setFormData({
        bundle_name: bundle.bundle_name,
        bundle_sku: bundle.bundle_sku,
        description: bundle.description || '',
        is_active: bundle.is_active,
        components,
      })
    } else {
      setEditingBundle(null)
      setFormData({
        bundle_name: '',
        bundle_sku: '',
        description: '',
        is_active: true,
        components: [],
      })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingBundle(null)
    setProductSearch('')
    setBarcodeInput('')
    setFormData({
      bundle_name: '',
      bundle_sku: '',
      description: '',
      is_active: true,
      components: [],
    })
  }

  // Handle barcode scanning for adding products
  const handleBarcodeScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const barcode = barcodeInput.trim()
      if (!barcode) return

      try {
        const result = await productService.getByBarcode(barcode)
        const data = result.data?.data

        if (data?.type === 'product') {
          const product = data.item
          // Check if already in bundle
          if (formData.components.find(item => item.product_id === product.product_id)) {
            toast.error('Product already in bundle')
            setBarcodeInput('')
            return
          }

          addComponent(product)
          toast.success(`Added ${product.product_name}`)
        } else if (data?.type === 'serial') {
          const product = data.product
          if (formData.components.find(item => item.product_id === product.product_id)) {
            toast.error('Product already in bundle')
            setBarcodeInput('')
            return
          }
          addComponent({ ...product, serial_number: data.serial_number })
          toast.success(`Added ${product.product_name} (${data.serial_number})`)
        } else {
          toast.error('Product not found')
        }
      } catch (error) {
        toast.error('Product not found')
      }
      setBarcodeInput('')
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.components.length === 0) {
      toast.error('Please add at least one component to the bundle')
      return
    }

    const submitData = {
      bundle_name: formData.bundle_name,
      bundle_sku: formData.bundle_sku,
      description: formData.description,
      is_active: formData.is_active,
      components: formData.components.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        is_optional: false,
      })),
    }

    if (editingBundle) {
      updateMutation.mutate({ id: editingBundle.bundle_id, data: submitData })
    } else {
      createMutation.mutate(submitData)
    }
  }

  const addComponent = (product: any) => {
    if (formData.components.find(item => item.product_id === product.product_id)) {
      toast.error('Product already in bundle')
      return
    }

    setFormData({
      ...formData,
      components: [...formData.components, {
        product_id: product.product_id,
        quantity: 1,
        product: {
          product_name: product.product_name,
          sku: product.sku,
          sale_price: product.sale_price,
        },
      }],
    })
    setProductSearch('')
  }

  const updateComponentQuantity = (productId: number, quantity: number) => {
    if (quantity < 1) return
    setFormData({
      ...formData,
      components: formData.components.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      ),
    })
  }

  const removeComponent = (productId: number) => {
    setFormData({
      ...formData,
      components: formData.components.filter(item => item.product_id !== productId),
    })
  }

  const calculateTotalValue = () => {
    return formData.components.reduce((sum, item) =>
      sum + (item.product?.sale_price || 0) * item.quantity, 0
    )
  }

  const openDeleteModal = (bundle: Bundle, e: React.MouseEvent) => {
    e.stopPropagation()
    setDeletingBundle(bundle)
    setShowDeleteModal(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Bundles</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage product bundles with special pricing</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/bundles/instances"
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CubeIcon className="w-5 h-5" />
            Instances
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            New Bundle
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search bundles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Bundles Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : bundles.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <GiftIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No bundles yet</h3>
          <p className="text-gray-500 mb-4">Create product bundles to offer special deals</p>
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Create your first bundle
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bundles.map((bundle: Bundle) => (
            <div
              key={bundle.bundle_id}
              onClick={() => openModal(bundle)}
              className="bg-white rounded-xl shadow border border-gray-100 p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-primary-50 rounded-xl">
                  <GiftIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex items-center gap-2">
                  {bundle.is_active ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Inactive</span>
                  )}
                  <button
                    onClick={(e) => openDeleteModal(bundle, e)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete bundle"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 mb-1">{bundle.bundle_name}</h3>
              <p className="text-sm text-gray-500 mb-3">{bundle.bundle_sku}</p>

              {bundle.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{bundle.description}</p>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="text-sm">
                  <span className="text-gray-500">Items:</span>{' '}
                  <span className="font-medium">{bundle.bundle_components?.length || 0}</span>
                </div>
                <div className="text-lg font-bold text-primary-600">
                  KSh {(bundle.calculations?.bundle_price ?? 0).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingBundle ? 'Edit Bundle' : 'New Bundle'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Bundle Details */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bundle Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.bundle_name}
                      onChange={(e) => setFormData({ ...formData, bundle_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., Starter Kit"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bundle Code *</label>
                    <input
                      type="text"
                      required
                      value={formData.bundle_sku}
                      onChange={(e) => setFormData({ ...formData, bundle_sku: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., BUNDLE-001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Describe what's included..."
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">Active</label>
                  </div>

                  {/* Bundle Summary */}
                  {formData.components.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Bundle Summary</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Total Items:</span>
                          <span className="font-medium">{formData.components.reduce((sum: number, item: BundleItem) => sum + item.quantity, 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Individual Value:</span>
                          <span className="font-medium">KSh {calculateTotalValue().toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Items */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bundle Items *</label>

                  {/* Barcode Scanner Input */}
                  <div className="relative mb-3">
                    <QrCodeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      ref={barcodeInputRef}
                      type="text"
                      placeholder="Scan barcode to add product..."
                      value={barcodeInput}
                      onChange={(e) => setBarcodeInput(e.target.value)}
                      onKeyDown={handleBarcodeScan}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Press Enter after scanning</p>

                  {/* Product Search */}
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Or search products to add..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  {/* Product Suggestions */}
                  {productSearch && availableProducts.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4 max-h-40 overflow-y-auto">
                      {availableProducts.map((product: any) => (
                        <button
                          key={product.product_id}
                          type="button"
                          onClick={() => addComponent(product)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <div className="font-medium text-sm">{product.product_name}</div>
                          <div className="text-xs text-gray-500">{product.sku} • KSh {product.sale_price?.toLocaleString()}</div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Selected Items */}
                  <div className="space-y-2">
                    {formData.components.map((item) => (
                      <div key={item.product_id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.product?.product_name}</p>
                          <p className="text-xs text-gray-500">{item.product?.sku} • KSh {item.product?.sale_price?.toLocaleString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateComponentQuantity(item.product_id, item.quantity - 1)}
                            className="p-1 border border-gray-300 rounded hover:bg-white"
                          >
                            -
                          </button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateComponentQuantity(item.product_id, item.quantity + 1)}
                            className="p-1 border border-gray-300 rounded hover:bg-white"
                          >
                            +
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeComponent(item.product_id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    {formData.components.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <CubeIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">Search and add products to this bundle</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-100 pt-4 mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingBundle
                      ? 'Update Bundle'
                      : 'Create Bundle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingBundle && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Bundle</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deletingBundle.bundle_name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deletingBundle.bundle_id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Bundle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
